/**
 * ConsentManager.Translator
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @lastModified 2026-06-30 Split translations into .json files per language, TypeScript cleaned.
 * @package fk_js/consentmanager
 * @since 5.1.0
 */
class Translator
{

    private language             :string = 'de';
    private translations         :Record<string, any> = {};
    private translationModules   :Record<string, { default: Record<string, unknown> }>;
    private translationOverrides :Record<string, any> = {};

    /**
     * Translator constructor.
     */
    public constructor(
        translationOverrides :object = {},
        language :null|string = null
    )
    {
        // @ts-ignore
        this.translationModules = import.meta.glob('../Translations/*.json', { eager: true }) || {};
        if (!language) {
            language = document.documentElement.getAttribute('lang') || 'en';
        }
        if (2 !== language.length) {
            language = this.language.substring(0, 2).toLowerCase();
        }
        this
            .setLanguage(language)
            .setTranslationOverrides(translationOverrides)
            .setTranslations(this.mergeTranslations());
    }

    /**
     * Merge translations from JSON files with translation overrides.
     */
    private mergeTranslations() :Record<string, any>
    {
        const translations :Record<string, any> = this.translations || {};
        Object.keys(this.translationModules).forEach((key :string) :void => {
            const language :string = key.split('.json')[0].slice(-2);
            translations[language] = {...translations[language], ...this.translationModules[key].default, ...this.translationOverrides[language]};
        });
        return translations;
    }

    /**
     * Replace all translatable keys within a string
     *
     * @param {string} string
     * @returns {string}
     */
    public replaceAll(string :string) :string
    {
        if (!this.translations[this.language]) {
            return string;
        }
        Object.keys(this.translations[this.language]).forEach((key) => {
            const value = this.translations[this.language][key];
            string = string.replace(`{${key}}`, value);
        })
        return string;
    }

    // #######
    // Getters
    // #######

    /**
     * Get a translation.
     */
    public get(key :string) :string
    {
        return this.translations[this.language]?.[key] || '';
    }

    /**
     * Get all translations.
     */
    public getAll() :Record<string, any>
    {
        return this.translations;
    }

    /**
     * Get current language
     *
     * @returns {string}
     */
    public getLanguage() :string
    {
        return this.language;
    }

    // #######
    // Setters
    // #######

    /**
     * Set a translation for given key.
     */
    set(key :string, value :string) :Translator
    {
        if (!this.translations[this.language]) {
            this.translations[this.language] = {};
        }
        this.translations[this.language][key] = value;
        return this;
    }

    /**
     * Set current language.
     */
    public setLanguage(language :string) :Translator
    {
        this.language = language;
        return this;
    }

    /**
     * Set the incoming translation overrides.
     */
    public setTranslationOverrides(translationOverrides :Record<string, any>) :Translator
    {
        this.translationOverrides = translationOverrides;
        return this;
    }

    /**
     * Setter for the translations object.
     */
    private setTranslations(translations :object) :Translator
    {
        this.translations = translations;
        return this;
    }

}

export default Translator;